// src/components/clients/AddClientDialog.tsx
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiService } from "@/api/api";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toggle } from "@/components/ui/toggle";
import type { Client } from "@/types/client"; 

const formSchema = z.object({
  first_name: z.string().min(2, { message: "First name must be at least 2 characters." }),
  last_name: z.string().min(2, { message: "Last name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }).or(z.literal("")).optional(),
  phone_number: z.string().optional(),
  status: z.string().optional(),
  primary_diagnosis: z.string().optional(),
  other_diagnoses: z.array(z.string()).max(5, { message: "Select up to five diagnoses." }).optional(),
  high_risk: z.boolean().optional(),
  other_client_factors: z.string().optional(),
  gender: z.enum(['Male', 'Female', 'Other']).optional(),
  date_of_birth: z
    .string()
    .optional()
    .refine(
      (val) => !val || !isNaN(new Date(val).getTime()),
      { message: "Please enter a valid date." }
    ),
});

type ClientFormValues = z.infer<typeof formSchema>;

interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: Client) => void; 
}

const AddClientDialog: React.FC<AddClientDialogProps> = ({ open, onOpenChange, onSave }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone_number: "",
      email: "",
      status: "Active",
      primary_diagnosis: "",
      other_diagnoses: [],
      high_risk: false,
      other_client_factors: "",
      gender: "Other",
      date_of_birth: "",
    },
  });

  React.useEffect(() => {
    if (open) {
      form.reset();
      setError(null);
    }
  }, [open, form]);

  const onSubmit = async (data: ClientFormValues) => {
    setLoading(true);
    setError(null);
    try {
      // ✅ Ensure date_of_birth is either a valid string or undefined
      const payload = {
        ...data,
        date_of_birth: data.date_of_birth || undefined,
      };
      const savedClient = await apiService.createClient(payload); 
      onSave(savedClient); 
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error saving client:", err);
      setError(err?.message || 'Failed to save client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add New Client</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* ✅ Date of Birth Field */}
            <FormField
              control={form.control}
              name="date_of_birth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      // Ensure value is in YYYY-MM-DD format
                      value={field.value || ""}
                      onChange={(e) => {
                        // HTML date inputs return empty string when cleared
                        field.onChange(e.target.value || undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="client@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="(123) 456-7890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="primary_diagnosis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary Diagnosis (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Major Depressive Disorder, Bipolar I" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="other_diagnoses"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Diagnoses and Comorbidities (up to five) separated by comma</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Major Depression, Generalized Anxiety, PTSD"
                      value={Array.isArray(field.value) ? field.value.join(', ') : ''}
                      onChange={e => {
                        const diagnoses = e.target.value.split(',').map(s => s.trim()).filter(Boolean).slice(0, 5);
                        field.onChange(diagnoses);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="high_risk"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between w-full">
                    <FormLabel className="mb-0">High risk client</FormLabel>
                    <FormControl>
                      <Toggle
                        pressed={field.value}
                        onPressedChange={field.onChange}
                        aria-label="High risk client"
                        className={`
                          w-12 h-7 rounded-full relative border-2 border-gray-300
                          bg-gray-200 transition-colors
                          data-[state=on]:bg-red-500 data-[state=on]:border-red-500
                          focus:outline-none
                        `}
                      >
                        <span
                          className={`
                            absolute left-0.5 top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform
                            transform
                            ${field.value ? "translate-x-5" : ""}
                          `}
                        />
                      </Toggle>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              control={form.control}
              name="other_client_factors"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Other Client Factors (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter any other client factors" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Client'}
              </Button>
            </DialogFooter>
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddClientDialog;